import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { language, notification, color, modal, noOp } from 'curvy';
import { Task } from '@task-utils/types';
import { TaskRoutes } from '@task-utils/routes';
import { get_global } from '@task-utils/utils';
import { meters_translations } from './meters.trans';

type Meter = TaskRoutes.meters.Meter;
type MeterType = TaskRoutes.meters.MeterType;
type Measurement = TaskRoutes.meters.MeterMeasurement;

interface ReadingImage { src: string; }
interface GroupedReading {
    timestamp: string;
    items: Measurement[];
    images: ReadingImage[];
    values: { [measure_id: number]: number };
    deltas: { [measure_id: number]: number };
}

export interface PendingMeter { code: string; brand: string; meter_type_id: number; type_name: string; }

interface MeterState {
    loaded: boolean;
    loading: boolean;
    measurements: Measurement[];
    readings: GroupedReading[];
}

enum MeterTypeName {
    ELECTRICITY = 'Električna energija',
    WATER = 'Voda',
    GAS = 'Plin',
}

const METER_TYPE_STYLE: Record<MeterTypeName, { icon: string; color: string }> = {
    [MeterTypeName.ELECTRICITY]: { icon: 'bolt', color: 'rgb(var(--yellow))' },
    [MeterTypeName.WATER]: { icon: 'opacity', color: 'rgb(var(--primary))' },
    [MeterTypeName.GAS]: { icon: 'local_fire_department', color: 'rgb(var(--orange))' },
};

const METER_TYPE_DEFAULT = { icon: 'av_timer', color: 'rgb(var(--background-dark))' };

@Component({
    selector: 'task-location-meters',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './location-meters.component.html',
    styleUrls: ['./location-meters.component.scss'],
})
export class LocationMetersComponent implements OnChanges {
    @Input() location: Partial<Task.Location> & { pending_meters?: PendingMeter[] } = {};

    meter_types: MeterType[] = [];
    meters: Meter[] = [];
    new_serial = "";
    new_brand = "";
    new_type_id: number | null = null;
    pending_meters: PendingMeter[] = [];

    state: { [meter_id: number]: MeterState } = {};

    constructor() {
        language.load_translations(meters_translations);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["location"]) {
            this.meters = [];
            this.state = {};
            this.load_types();
            if (this.location?.location_id) {
                this.load_meters(this.location.location_id);
            } else {
                this.pending_meters = this.location?.pending_meters || [];
            }
        }
    }

    async load_types() {
        try {
            let res = await TaskRoutes.meters.api_meter_type_get_all({ page_no: 1, page_size: 100 });
            this.meter_types = res.data || [];
            if (this.meter_types.length && this.new_type_id == null) {
                this.new_type_id = this.meter_types[0].meter_type_id;
            }
        } catch { this.meter_types = []; }
    }

    async load_meters(location_id: number) {
        try {
            let res = await TaskRoutes.meters.api_meter_search(
                { search: [location_id], attributes: ["#=:location_id"] },
                { page_no: 1, page_size: 200 }
            );
            this.meters = res.data || [];
            for (let m of this.meters) { this.state_of(m); }
        } catch { this.meters = []; }
    }

    async add_meter() {
        let code = this.new_serial.trim();
        let brand = this.new_brand.trim();
        if (!code || !brand || !this.new_type_id) { return; }

        let exists = this.pending_meters.some(p => p.code.toLowerCase() === code.toLowerCase())
            || this.meters.some(m => m.code.toLowerCase() === code.toLowerCase());

        if (exists) {
            notification.show({
                icon: 'error',
                color: color.Variable.warn,
                title: language.translate('ERROR'),
                message: language.translate('METERS.DUPLICATE_SERIAL', code),
            });
            return;
        }

        if (!this.location?.location_id) {
            let type = this.meter_types.find(t => t.meter_type_id === this.new_type_id);
            this.pending_meters.push({ code, brand, meter_type_id: this.new_type_id, type_name: type?.name ?? '' });
            this.location.pending_meters = this.pending_meters;
            this.new_serial = "";
            this.new_brand = "";
            return;
        }

        try {
            await TaskRoutes.meters.api_meter_add({
                code,
                brand,
                meter_type_id: this.new_type_id,
                location_id: this.location.location_id,
            });
            this.new_serial = "";
            this.new_brand = "";
            await this.load_meters(this.location.location_id);
        } catch (e: any) {
            notification.show({
                icon: 'error',
                color: color.Variable.warn,
                title: language.translate('ERROR'),
                message: e?.message ?? '',
            });
        }
    }

    remove_pending(index: number) {
        this.pending_meters.splice(index, 1);
        this.location.pending_meters = this.pending_meters;
    }

    type_color(type_name: string) {
        return meter_type_style(type_name).color;
    }

    type_icon(type_name: string) {
        return meter_type_style(type_name).icon;
    }

    is_recorded(meter: Meter): boolean {
        let measures = meter.measures || [];
        return measures.length > 0 && measures.every(m => m.value !== null && m.value !== undefined);
    }

    measure_value(g: GroupedReading, measure_id: number): string {
        let v = g.values[measure_id];
        return v === undefined ? '–' : `${v}`;
    }

    delta_abs(d: number): number {
        return Math.abs(d);
    }

    open_images(g: GroupedReading) {
        const ImageDialog = get_global("ImageDialog");
        modal.open(ImageDialog, noOp, { imgs: g.images, index: 0 });
    }

    state_of(meter: Meter): MeterState {
        if (!this.state[meter.meter_id]) {
            this.state[meter.meter_id] = { loaded: false, loading: false, measurements: [], readings: [] };
        }
        return this.state[meter.meter_id];
    }

    async handle_toggle(meter: Meter, ev: Event) {
        let el = ev.target as HTMLDetailsElement;
        let st = this.state_of(meter);
        if (el.open && !st.loaded && !st.loading) {
            await this.load_measurements(meter);
        }
    }

    async load_measurements(meter: Meter) {
        let st = this.state_of(meter);
        st.loading = true;
        try {
            let res = await TaskRoutes.meters.api_meter_measurements_search(
                meter.meter_id,
                { search: [], attributes: [], sort: [{ attribute: 'timestamp', ascending: false }] },
                { page_no: 1, page_size: 200 }
            );
            st.measurements = res.data || [];
            st.readings = group_readings(st.measurements);
            st.loaded = true;
        } catch { st.measurements = []; st.readings = []; }
        st.loading = false;
    }

    track_meter = (_: number, m: Meter) => m.meter_id;
    track_reading = (_: number, g: GroupedReading) => g.timestamp;
}


function meter_type_style(type_name: string) {
    return METER_TYPE_STYLE[type_name as MeterTypeName] ?? METER_TYPE_DEFAULT;
}

function group_readings(measurements: Measurement[]): GroupedReading[] {
    let map = new Map<string, Measurement[]>();
    for (let mm of measurements) {
        if (!map.has(mm.timestamp)) { map.set(mm.timestamp, []); }
        map.get(mm.timestamp)!.push(mm);
    }

    let groups: GroupedReading[] = [];
    for (let [timestamp, items] of map) {
        let values: { [id: number]: number } = {};
        let images: ReadingImage[] = [];
        for (let it of items) {
            values[it.measure_id] = it.value;
            if (it.picture_url) {
                images.push({ src: TaskRoutes.upload.uploaded_file_get(it.picture_url) });
            }
        }
        groups.push({ timestamp, items, values, deltas: {}, images });
    }

    for (let i = 0; i < groups.length; i++) {
        let prev = groups[i + 1];
        if (!prev) { continue; }
        for (let id in groups[i].values) {
            if (prev.values[id] !== undefined) {
                groups[i].deltas[id] = groups[i].values[id] - prev.values[id];
            }
        }
    }

    return groups;
}
